import {Button, Modal, Form} from "react-bootstrap";
import {t} from "ttag";
import {createRoot} from 'react-dom/client'
import React, {
  ChangeEvent, InputHTMLAttributes, KeyboardEvent,
  ReactNode,
  RefObject,
  useEffect,
  useRef,
  useState
} from "react";
import Editor from "./Editor";
import {isEqual} from "lodash";

function ConfirmationDialog({closeDialog, label, text, confirmText} : {
  closeDialog: (approved: boolean) => void,
  label: string,
  text: string,
  confirmText: string
}) {
  return <Modal show={true} onHide={() => closeDialog(false)}>
    <Modal.Header closeButton>
      <Modal.Title>{label}</Modal.Title>
    </Modal.Header>
    <Modal.Body><div dangerouslySetInnerHTML={{__html: text}}/></Modal.Body>
    <Modal.Footer>
      <Button variant="secondary" onClick={() => closeDialog(false)}>
        {t`Close`}
      </Button>
      <Button variant="primary" onClick={() => closeDialog(true)}>
        {confirmText}
      </Button>
    </Modal.Footer>
  </Modal>
}

export function confirmDialog({label, text, buttonText = t`Confirm`, onConfirm} : {
  label: string,
  text: string,
  buttonText?: string,
  onConfirm?: () => void
}) {
  let resolver : (value: boolean) => void
  const returnedPromise = new Promise<boolean>(resolve => {
    resolver = resolve
  })

  const container = document.createElement("div");
  const root = createRoot(container);
  async function onClose(accepted : boolean) {
    root.unmount();
    container.remove();
    if(accepted && onConfirm) {
      await onConfirm();
    }
    resolver(accepted)
  }

  root.render(<ConfirmationDialog closeDialog={onClose} label={label} text={text} confirmText={buttonText} />);
  return returnedPromise;
}

function PromptDialog({closeDialog, label, text, confirmText, defaultValue, setValue, richText, additionalText, options, size, customInputParams}: {
  closeDialog: (approved: boolean) => void,
  label: string,
  text: string,
  confirmText?: string,
  defaultValue: string,
  setValue: (val: string) => void,
  richText?: boolean,
  additionalText?: string,
  options?: ReactNode[],
  size?: "lg" | "sm" | "xl",
  customInputParams: Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'value'>,
}) {
  const [state, setState] = useState(defaultValue);
  // https://stackoverflow.com/a/61980548
  const inputRef = useRef<HTMLElement>(null);
  useEffect(() => {
    inputRef.current && inputRef.current.focus()
  });

  function updateValue(val : string) {
    setValue(val)
    setState(val)
  }

  const baseFormProps: {
    defaultValue?: string,
    value?: string,
    onChange: (e : ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void,
    onKeyUp: (e : KeyboardEvent) => void
  } = {
    value: state,
    onChange: (e : ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>{
      updateValue(e.target.value)
    },
    onKeyUp: (e : KeyboardEvent) => {
      if (e.key === 'Enter'){
        closeDialog(true);
      }
    }
  }

  if(customInputParams.type === 'datetime-local') {
    // datetime-local in chrome does not have great handling as a controlled component, so make it uncontrolled and avoid rerenders
    baseFormProps.defaultValue = defaultValue;
    delete baseFormProps.value;
    baseFormProps.onChange = (e) => {
      // typing 0 causes the input to see '' as the input here, and that's likely to make other layers of the system sad
      if(e.target.value == '') return;
      setValue(e.target.value)
    }
  }

  let control;
  if(options) {
    control = <Form.Control ref={inputRef as RefObject<HTMLSelectElement>} as="select" {...baseFormProps}>{options}</Form.Control>
  } else if(richText) {
    control = <Editor value={state} onChange={(value) => updateValue(value)} withImages />
  } else {
    control = <Form.Control ref={inputRef as RefObject<HTMLInputElement>} {...customInputParams} {...baseFormProps} />
  }

  return <Modal size={size ? size : (richText ? 'lg' : undefined)} show={true} onHide={() => closeDialog(false)}>
    <Modal.Header closeButton>
      <Modal.Title>{label}</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      {text}
      <Form.Group>
        {control}
      </Form.Group>
      {additionalText}
    </Modal.Body>
    <Modal.Footer>
      <Button variant="secondary" onClick={() => closeDialog(false)}>
        {t`Close`}
      </Button>
      <Button variant="primary" onClick={() => closeDialog(true)}>
        {confirmText}
      </Button>
    </Modal.Footer>
  </Modal>
}

export function promptDialog({label, text, buttonText, defaultValue = '', onSubmit, richText, additionalText, options, size, customInputParams = {}} : {
  label: string,
  text: string,
  buttonText?: string,
  defaultValue?: string,
  onSubmit?: (val: string) => void,
  richText?: boolean,
  additionalText?: string,
  options?: ReactNode[],
  size?: "lg" | "sm" | "xl",
  customInputParams?: Omit<InputHTMLAttributes<HTMLInputElement>, 'size'>,
}) {
  let resolver : (value: string | null) => void
  const returnedPromise = new Promise<string | null>(resolve => {
    resolver = resolve
  })

  const state = {
    value: defaultValue
  }

  const container = document.createElement("div");
  const root = createRoot(container);
  async function onClose(accepted : boolean) {
    root.unmount();
    container.remove();
    if(accepted && onSubmit) {
      await onSubmit(state.value);
    }
    resolver(accepted ? state.value : null)
  }

  root.render(<PromptDialog closeDialog={onClose} label={label} text={text} confirmText={buttonText}
                            defaultValue={defaultValue} richText={richText} additionalText={additionalText}
                            setValue={(val) => state.value = val}
                            options={options} size={size} customInputParams={customInputParams}/>);
  return returnedPromise;
}

function MessageDialog({closeDialog, label, text, dangerous = false, size} : {
  closeDialog: () => void,
  label: string,
  text: ReactNode,
  dangerous?: boolean,
  size?: 'sm' | 'lg' | 'xl';
}) {
  // Would be nice if we can kill off dangerous at some point, but that only works if all callers are react-based
  return <Modal show={true} onHide={() => closeDialog()} size={size}>
    <Modal.Header closeButton>
      <Modal.Title>{label}</Modal.Title>
    </Modal.Header>
    <Modal.Body>{dangerous ? <div dangerouslySetInnerHTML={{__html: text as String}}/>: text}</Modal.Body>
    <Modal.Footer>
      <Button variant="secondary" onClick={() => closeDialog()}>
        {t`OK`}
      </Button>
    </Modal.Footer>
  </Modal>
}

export function messageDialog({label, text, dangerous = false, size} : {
  label: string,
  text: ReactNode,
  dangerous? : boolean,
  size?: 'sm' | 'lg' | 'xl'
}) {
  const container = document.createElement("div");
  const root = createRoot(container);
  async function onClose() {
    root.unmount()
    container.remove();
  }

  root.render(<MessageDialog closeDialog={onClose} label={label} text={text} dangerous={dangerous} size={size}/>);
}

export function useUnsavedChanges<T>(baseline: T, current: T, isDirty?: (a:T , b:T) => boolean) {
    // Use a ref to store the current value of hasUnsavedChanges
    const baseRef = useRef(baseline);
    const currentRef = useRef(current);

    if (!isDirty) {
        isDirty = (a, b) => !isEqual(a,b);
    }

    // Update the ref whenever either value changes
    useEffect(() => {
        baseRef.current = baseline;
        currentRef.current = current;
    }, [baseline, current]);

    useEffect(() => {
        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            if (isDirty(baseRef.current, currentRef.current)) {
                event.preventDefault();
                // Modern browsers display a generic message, but set returnValue
                // for compatibility with some older versions.
                event.returnValue = '';
            }
        };

        // Add the event listener when the component mounts
        window.addEventListener('beforeunload', handleBeforeUnload);

        // Clean up the event listener when the component unmounts
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, []);
}